import {
  unref,
  provide,
  inject,
  onMounted,
  onBeforeUnmount,
} from 'vue';

import Observer from '@/services/observer';

/**
 * Custom hook to create and provide an Observer instance.
 *
 * This hook initializes an Observer instance, provides it using the given key,
 * and ensures that all handlers are unregistered before the component is unmounted.
 *
 * @param {Object} params - The parameters object.
 * @param {Ref|string} params.key - The key used to provide the Observer instance.
 *                                  It can be a ref or a plain string.
 *
 * @returns {Object} An object containing the Observer instance.
 * @returns {Observer} return.observer - The created Observer instance.
 */
export const useObserver = ({ key }) => {
  const observer = new Observer();

  provide(unref(key), observer);

  onBeforeUnmount(() => observer.unregisterAll());

  return {
    observer,
  };
};

/**
 * Custom hook to inject an Observer instance and automatically register/unregister a child handler.
 *
 * This hook injects an Observer instance using the given key, registers the provided handler
 * as a child in onMounted, and unregisters it in onBeforeUnmount.
 *
 * @param {Object} params - The parameters object.
 * @param {Function} params.handler - The handler function to register as a child.
 * @param {Ref|string} [params.key='$refresh'] - The key used to inject the Observer instance.
 *                                              It can be a ref or a plain string.
 *
 * @returns {Object} An object containing the Observer instance.
 * @returns {Observer} return.observer - The injected Observer instance.
 */
export const useChildObserver = ({ handler, key = '$refresh' }) => {
  const observer = inject(unref(key), new Observer());

  onMounted(() => {
    observer.registerChild(handler);
  });

  onBeforeUnmount(() => {
    observer.unregisterChild(handler);
  });

  return {
    observer,
  };
};
